import React, { useEffect, useState } from "react";
import Layout from "@/components/layout/Layout";
import { api, formatNumber } from "@/lib/api";
import { BarChart3, Activity, Flame } from "lucide-react";

function MiniBars({ data, max, color = "var(--ks-primary)", label }) {
  const m = max || Math.max(1, ...data.map((d) => d.v));
  return (
    <div className="flex items-end gap-1 h-32 w-full">
      {data.map((d, i) => (
        <div key={i} className="flex-1 flex flex-col items-center justify-end" title={`${label}: ${formatNumber(d.v)}`}>
          <div
            style={{ height: `${(d.v / m) * 100}%`, background: color, minHeight: 2, width: "100%", borderRadius: 2, opacity: 0.85 }}
          />
          <div className="text-[9px] text-[var(--ks-text-soft)] mt-1">{d.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function Charts() {
  const [stats, setStats] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
    api.get("/charts/summary").then((r) => setSummary(r.data));
  }, []);

  if (!stats || !summary) return <Layout><p>Loading…</p></Layout>;

  const txBars = summary.tx_series.map((s) => ({ v: s.count, label: s.day === 0 ? "today" : `-${s.day}d` }));
  const gasBars = summary.gas_series.map((s) => ({ v: Math.round((s.used / Math.max(1, s.limit)) * 100), label: `#${s.number}` }));
  const total = summary.status_counts.Success + summary.status_counts.Pending + summary.status_counts.Failed || 1;
  const succPct = ((summary.status_counts.Success / total) * 100).toFixed(1);
  const pendPct = ((summary.status_counts.Pending / total) * 100).toFixed(1);
  const failPct = ((summary.status_counts.Failed / total) * 100).toFixed(1);

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-4">
        <BarChart3 className="text-[var(--ks-primary)]" />
        <h1 className="text-xl font-semibold" data-testid="charts-title">Charts &amp; Stats</h1>
      </div>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="ks-stat-tile"><div><div className="ks-stat-label">ETH Price</div><div className="ks-stat-value">${formatNumber(stats.eth_price_usd)}</div></div></div>
        <div className="ks-stat-tile"><div><div className="ks-stat-label">Market Cap</div><div className="ks-stat-value">${formatNumber(stats.market_cap_billions)}B</div></div></div>
        <div className="ks-stat-tile"><div><div className="ks-stat-label">Total Txs</div><div className="ks-stat-value">{formatNumber(stats.total_transactions)}</div></div></div>
        <div className="ks-stat-tile"><div><div className="ks-stat-label">Active Wallets</div><div className="ks-stat-value">{formatNumber(stats.active_wallets)}</div></div></div>
      </section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="ks-card p-4" data-testid="chart-txs">
          <div className="flex items-center gap-2 mb-2"><Activity size={14} className="text-[var(--ks-primary)]"/><span className="font-semibold">Transactions per Day (14d)</span></div>
          <MiniBars data={txBars} label="txs" />
        </div>
        <div className="ks-card p-4" data-testid="chart-gas">
          <div className="flex items-center gap-2 mb-2"><Flame size={14} className="text-orange-500"/><span className="font-semibold">Block Gas Utilization %</span></div>
          <MiniBars data={gasBars} label="gas %" color="#f59e0b" max={100} />
        </div>
      </div>

      <div className="ks-card p-4" data-testid="chart-status">
        <div className="font-semibold mb-2">Transaction Status Distribution (last 500)</div>
        <div className="flex w-full h-6 rounded overflow-hidden border border-[var(--ks-border)]">
          <div style={{ width: `${succPct}%`, background: "var(--ks-success)" }} title={`Success ${succPct}%`} />
          <div style={{ width: `${pendPct}%`, background: "#f59e0b" }} title={`Pending ${pendPct}%`} />
          <div style={{ width: `${failPct}%`, background: "var(--ks-failed)" }} title={`Failed ${failPct}%`} />
        </div>
        <div className="flex gap-4 text-xs mt-2">
          <span><span className="inline-block w-3 h-3 rounded-sm mr-1" style={{background:"var(--ks-success)"}}/> Success {summary.status_counts.Success} ({succPct}%)</span>
          <span><span className="inline-block w-3 h-3 rounded-sm mr-1" style={{background:"#f59e0b"}}/> Pending {summary.status_counts.Pending} ({pendPct}%)</span>
          <span><span className="inline-block w-3 h-3 rounded-sm mr-1" style={{background:"var(--ks-failed)"}}/> Failed {summary.status_counts.Failed} ({failPct}%)</span>
        </div>
      </div>
    </Layout>
  );
}
